import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { IEventDetailsDTO } from '../../../../../../../api/events.dao';
import { LONG_RICH_TEXT_LENGTH, SHORT_PLAIN_TEXT_LENGTH } from '../../../../../../../constants/common.constants';
import { calcByteCodeSize } from '../../../../../../../infrastructure/utils/common.utils';
import {
    ICreateScheduleEmailDTO, IScheduledEmailDTO, IScheduledEmailTemplateDTO, IUpdateScheduleEmailDTO, ScheduledTypes
} from '../../../../../../../api/scheduled-email.dao';
import { utcToZonedTime, zonedTimeToUtc } from '../../../../../../../infrastructure/utils/date-fns-overrides.utils';
import {
    scheduledEmailBody as scheduledEventEmailBody,
    scheduledEmailSubject as scheduledEventEmailSubject
} from '../_placeholder/scheduled-event-email-template';
import {
    scheduledEmailBody as scheduledMeetingEmailBody,
    scheduledEmailSubject as scheduledMeetingEmailSubject
} from '../_placeholder/scheduled-meeting-email-template';
import { isAfter } from 'date-fns';
import { IGlobalStorage } from '../../../../../../../infrastructure/global-storage';
import UploadImageS3VM from '../../../../shared/upload-image/upload-image-to-s3.viewmodel';
import {
    DATE_AND_TIME_IS_REQUIRED_ERROR_TEXT,
    DATE_AND_TIME_SHOULD_NOT_BE_IN_THE_PAST_ERROR_TEXT,
    EMAIL_BODY_REQUIRED_ERROR_TEXT,
    EMAIL_BODY_TOO_LONG_ERROR_TEXT,
    EMAIL_SUBJECT_REQUIRED_ERROR_TEXT,
    EMAIL_SUBJECT_TOO_LONG_ERROR_TEXT,
    INVALID_TIME_ERROR_TEXT,
    SHORT_PLAIN_TEXT_TOO_LONG_ERROR_TEXT
} from './scheduled-email-form.texts';
import { IMeetingData } from './scheduled-email-form.viewmodel';
import { capitalizeFirstLetter } from '../../../../../../../infrastructure/utils/string.utils';
import { IReactIntlProvider } from '../../../../../../../infrastructure/react-intl-provider';
import { DateTimeFormatter } from '../../../../../../../infrastructure/implementations/date-formatter';
import { DateFnsFormats, IDateTimeFormatter } from '../../../../../../../infrastructure/date-formatter';
import { transpileInlineStylesToClasses } from '../../../../../../global/rich-text-editor/utils/transpiler';

class ScheduledEmailDataVM {
    private static isDefaultEventEmailTemplate(template: IScheduledEmailTemplateDTO): boolean {
        return (
            template.subject === scheduledEventEmailSubject &&
            template.body === scheduledEventEmailBody &&
            template.emailImage === null
        );
    }

    private static isDefaultMeetingEmailTemplate(template: IScheduledEmailTemplateDTO): boolean {
        return (
            template.subject === scheduledMeetingEmailSubject &&
            template.body === scheduledMeetingEmailBody &&
            template.emailImage === null
        );
    }

    @mobx.observable
    public name: string;
    @mobx.observable
    public date: Date;
    @mobx.observable
    public dateTime: Date;
    @mobx.observable
    public emailSubject: string;
    @mobx.observable
    public emailBody: string;
    @mobx.observable
    public emailSubjectError: string = '';
    @mobx.observable
    public timeValidationError: boolean = false;
    @mobx.observable
    public formChecked: boolean;
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    public uploadEmailImageS3VM: UploadImageS3VM = null;
    @mobx.observable
    private notifyDeliveryTeam: boolean;

    private templateId: string | undefined;

    public constructor(
        private _event: IEventDetailsDTO,
        private scheduledEmailDTO: Partial<IScheduledEmailDTO> = {},
        private type: ScheduledTypes,
        private meetingData: IMeetingData,
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject protected readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject protected readonly dateFormatter: IDateTimeFormatter = IoC.Container.get(DateTimeFormatter)
    ) {
        this.event = _event;
        mobx.reaction(() => this.globalStorage.getEvent(), () => {
            this.event = this.globalStorage.getEvent();
        });

        this.fromDTO(scheduledEmailDTO);

        this.uploadEmailImageS3VM = new UploadImageS3VM(this.event.accountId, this.event.eventId, 'emailImage', 'scheduled-emails');
        this.setLogoImage(scheduledEmailDTO);
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public isImageUploading = (): boolean => {
        return this.uploadEmailImageS3VM.isImageUploading;
    };

    public getNotifyDeliveryTeam = (): boolean => {
        return this.notifyDeliveryTeam;
    };

    @mobx.action
    public setNotifyDeliveryTeam = (value: boolean): void => {
        this.notifyDeliveryTeam = value;
    };

    @mobx.action
    public checkForm(): boolean {
        this.formChecked = true;
        return !this.getDateError() &&
            !this.isEmailSubjectError() &&
            !this.getEmailBodyError() &&
            !this.getNameError();
    }

    @mobx.action
    public setDate(date?: Date) {
        this.date = date;
        if (date) {
            this.dateTime.setFullYear(this.date.getFullYear(), this.date.getMonth(), this.date.getDate());
        }
    }

    @mobx.action
    public setTime(date: Date) {
        this.timeValidationError = false;
        this.dateTime = new Date(this.date.getTime());
        this.dateTime.setHours(date.getHours(), date.getMinutes());
    }

    @mobx.action
    public setEmailSubject(subject: string) {
        this.emailSubject = subject;
        this.setEmailSubjectError(subject);
    }

    @mobx.action
    public setEmailBody(body: string) {
        this.emailBody = body;
    }

    @mobx.action
    public setName(value: string) {
        this.name = value;
    }

    @mobx.action
    public setEmailSubjectError(value: string): void {
        if (value.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            this.emailSubjectError = this.intl.formatMessage(EMAIL_SUBJECT_TOO_LONG_ERROR_TEXT);
        } else if (!value.trim().length) {
            this.emailSubjectError = this.intl.formatMessage(EMAIL_SUBJECT_REQUIRED_ERROR_TEXT);
        } else {
            this.emailSubjectError = '';
        }
    }

    public isEmailSubjectError(): boolean {
        return !!this.emailSubjectError.length;
    }

    public getEmailBodyError(): string {
        if (calcByteCodeSize(this.emailBody) > LONG_RICH_TEXT_LENGTH) {
            return this.intl.formatMessage(EMAIL_BODY_TOO_LONG_ERROR_TEXT);
        } else if (!calcByteCodeSize(this.emailBody)) {
            return this.intl.formatMessage(EMAIL_BODY_REQUIRED_ERROR_TEXT);
        }

        return null;
    }

    public getNameError(): string {
        if (this.name.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            return this.intl.formatMessage(SHORT_PLAIN_TEXT_TOO_LONG_ERROR_TEXT);
        }

        return null;
    }

    public getDateError(): string | null {
        const currentDateInEventTimezone = utcToZonedTime(new Date(), this.event.timeZone);

        if (!this.date) {
            return this.intl.formatMessage(DATE_AND_TIME_IS_REQUIRED_ERROR_TEXT);
        }

        if (this.timeValidationError) {
            return this.intl.formatMessage(INVALID_TIME_ERROR_TEXT);
        }

        if (isAfter(currentDateInEventTimezone, this.dateTime)) {
            return this.intl.formatMessage(DATE_AND_TIME_SHOULD_NOT_BE_IN_THE_PAST_ERROR_TEXT);
        }

        return null;
    }

    public toCreateApiDTO(): ICreateScheduleEmailDTO {
        const template = this.templateToDTO();
        const isDefaultTemplate = ScheduledEmailDataVM.isDefaultEventEmailTemplate(template)
            || ScheduledEmailDataVM.isDefaultMeetingEmailTemplate(template);

        return {
            dateTime: zonedTimeToUtc(this.dateTime, this.event.timeZone),
            name: this.name.trim() || this.getDefaultTemplateName(),
            emailTemplate: isDefaultTemplate ? null : template,
            notifyDeliveryTeam: this.notifyDeliveryTeam
        };
    }

    public toUpdateApiDTO(): IUpdateScheduleEmailDTO {
        const data = this.toCreateApiDTO();
        const meetingInfo = this.type === ScheduledTypes.Meeting && this.meetingData
            ? {
                meetingId: this.meetingData.meetingId,
                roles: this.meetingData.roles
            }
            : null;
        return { ...data, id: this.scheduledEmailDTO.id, ...meetingInfo };
    }

    private templateToDTO(): IScheduledEmailTemplateDTO {
        return {
            subject: this.emailSubject,
            body: transpileInlineStylesToClasses(this.emailBody),
            emailImage: this.getLogoImage(),
            ...(this.templateId && { id: this.templateId })
        };
    }

    private fromDTO = (dto: Partial<IScheduledEmailDTO>): void => {
        const dateTimeValue = dto.dateTime ? utcToZonedTime(this.scheduledEmailDTO.dateTime, this.event.timeZone) : this.getInitDate();
        const defaultEmailTemplate = this.getDefaultEmailTemplate();

        this.name = dto.name || this.getTemplateName();
        this.emailBody = dto.emailTemplate?.body || defaultEmailTemplate.body;
        this.emailSubject = dto.emailTemplate?.subject || defaultEmailTemplate.subject;
        this.templateId = dto.emailTemplate?.id;
        this.notifyDeliveryTeam = dto.notifyDeliveryTeam ?? true;

        this.date = this.dateTime = dateTimeValue;
    };

    private getTemplateName = (): string => {
        return this.type === ScheduledTypes.Meeting ? this.getMeetingTemplateName() : this.getDefaultTemplateName();
    };

    private getDefaultEmailTemplate = () => {
        return {
            subject: this.type === ScheduledTypes.Event ? scheduledEventEmailSubject : scheduledMeetingEmailSubject,
            body: this.type === ScheduledTypes.Event ? scheduledEventEmailBody : scheduledMeetingEmailBody,
            emailImage: null
        };
    };

    private getDefaultTemplateName = (): string => {
        return `${capitalizeFirstLetter(this.type)} Reminder Email`;
    };

    private getMeetingTemplateName = (): string => {
        const date = this.dateFormatter.dateToFormattedString(
            this.meetingData.startDateTime,
            DateFnsFormats.ShortMonthDay
        );
        const topicOrPresenterName = this.meetingData.topic || this.meetingData.presenters.map(x => x.name).join(', ');
        return date && topicOrPresenterName
            ? `${date} ${topicOrPresenterName} Reminder`
            : `${date} ${this.getDefaultTemplateName()}`;
    };

    private getLogoImage(): { title: string; objectKey: string } | null {
        const { emailImage } = this.uploadEmailImageS3VM.getImageToApi();
        return emailImage;
    }

    private setLogoImage(scheduledEmail: Partial<IScheduledEmailDTO>): void {
        if (scheduledEmail.emailTemplate?.emailImage && scheduledEmail.emailTemplate?.emailImageWithUrl) {
            this.uploadEmailImageS3VM.addImage(
                scheduledEmail.emailTemplate.emailImageWithUrl.assetUrl,
                scheduledEmail.emailTemplate.emailImage.title,
                scheduledEmail.emailTemplate.emailImage.objectKey
            );
        }
    }

    private getInitDate(): Date {
        const currentDateInEventTimezone = utcToZonedTime(new Date(), this.event.timeZone);
        const time = this.type === ScheduledTypes.Event ? this.event.startDateTime : this.meetingData?.startDateTime;
        let date = utcToZonedTime(time, this.event.timeZone);

        date.setHours(date.getHours() - 1);

        if (currentDateInEventTimezone > date) {
            date = currentDateInEventTimezone;
            date.setHours(date.getHours() + 1);
        }

        return date;
    }
}

export { ScheduledEmailDataVM };

